import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Autocomplete from '@web-ui-kit/autocomplete'
import Text from '@web-ui-kit/text'
import * as Sc from './CourseForm.style'
import useMediaQuery from 'hooks/useMediaQuery'
import {
  useGetUserCourses,
  usePostAddCourseToUser,
  USER_KEYS,
} from 'hooks/user'
import {
  useGetStudentCourses,
  usePostCourseStudent,
  useGetChildrenById,
} from 'hooks/student'
import { ROUTES } from 'routes/paths'
import { updateFullScreen } from 'store/actions/drawer.actions'
import { fetchStudentListAction } from 'store/actions/student.actions'
import { useSnackBarFeedback } from 'context/snackbarFeedback'
import { formatCurseName, getUrlParams } from 'utils'
import { DEVICE_SIZE, TYPES_USER_ROLES } from 'utils/constants'
import { TYPES_ALERTS } from 'utils/constants/global'

const CourseForm = () => {
  const queryClient = useQueryClient()
  const addFeedback = useSnackBarFeedback()
  const [course, setCourse] = useState({
    id: '',
    value: null,
  })
  const { push } = useHistory()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const isTablet = useMediaQuery(`(max-width: ${DEVICE_SIZE.TABLET_S})`)
  const studentId = getUrlParams('id')
  const urlPath = getUrlParams('origin')
  const originPath = urlPath ?? ROUTES.BASE

  const {
    course: userCourse,
    id: userId,
    role,
  } = queryClient.getQueryData({
    queryKey: [USER_KEYS.GET_USER],
  }) || {}

  const isRelative = role === TYPES_USER_ROLES.RELATIVE
  const { data: selectedStudent } = useGetChildrenById({
    childrenId: studentId,
    enabled: isRelative,
  })

  const size = isTablet ? 'small' : 'big'

  const { data: coursesUser } = useGetUserCourses({
    enabled: !isRelative,
    userId,
  })

  const { data: coursesStudent } = useGetStudentCourses({
    enabled: !!(isRelative && selectedStudent?.school?.id),
    schoolID: selectedStudent?.school?.id,
  })

  const { mutate: mutateAddCourse, isLoading: isLoadingAddCourse } =
    usePostAddCourseToUser()
  const {
    mutate: mutateAddCourseStudent,
    isLoading: isLoadingAddCourseStudent,
  } = usePostCourseStudent()

  useEffect(() => {
    if (
      (!isRelative && userCourse?.id) ||
      (isRelative && selectedStudent?.course)
    ) {
      push(ROUTES.BASE)
    } else {
      dispatch(updateFullScreen(true))
    }
    return () => {
      dispatch(updateFullScreen(false))
    }
  }, [])

  useEffect(() => {
    if (
      (!isRelative && userCourse?.id) ||
      (isRelative && selectedStudent?.course)
    ) {
      push(ROUTES.BASE)
    }
  }, [userCourse, selectedStudent])

  const handleOnChange = (value, key) => {
    setCourse({ ...course, id: key, value })
  }

  const getOptionsCourses = () => {
    const courses = isRelative ? coursesStudent?.courses : coursesUser
    const optionsList = courses?.length
      ? courses.map((course) => ({
          key: course.id,
          value: formatCurseName({ course }),
        }))
      : []
    return optionsList
  }

  const handleOnSubmit = async (event) => {
    event.preventDefault()
    if (isRelative) {
      mutateAddCourseStudent(
        {
          courseId: course.id,
          studentId: studentId,
        },
        {
          onError: () => {
            addFeedback({
              dataTestId: 'error-feedback-course',
              id: 'error-feedback-course',
              text: t('Ha ocurrido un problema'),
              variant: TYPES_ALERTS.ERROR,
            })
          },
          onSuccess: () => {
            dispatch(fetchStudentListAction(true))
            push(
              `${ROUTES.SELECT_STUDENT_GROUP}/${studentId}?origin=${originPath}`
            )
          },
        }
      )
    } else {
      mutateAddCourse(
        {
          courseId: course.id,
          isEnabledBff: true,
        },
        {
          onError: () => {
            addFeedback({
              dataTestId: 'error-feedback-course',
              id: 'error-feedback-course',
              text: t('Ha ocurrido un problema'),
              variant: TYPES_ALERTS.ERROR,
            })
          },
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [USER_KEYS.GET_USER],
            })
            push(
              `${ROUTES.SELECT_STUDENT_GROUP}/${userId}?origin=${originPath}`
            )
          },
        }
      )
    }
  }

  const handleCancel = () => {
    const redirectUrl = originPath === pathname ? ROUTES.BASE : originPath
    push(redirectUrl)
  }

  return (
    <Sc.Container>
      <Text variant={isTablet ? 'title3Mobile' : 'title3'}>
        {t('Selecciona tu curso')}
      </Text>
      <Text variant='body'>
        {t('Si todavía no sabes el curso al que vas, pídeselo a tu profesor/a')}
      </Text>
      <Sc.Form>
        <Autocomplete
          dataTestId='select-course'
          id={'course'}
          label={t('Curso')}
          noOptionsText={t('No hay opciones')}
          onChange={({ key, value }) => handleOnChange(value, key)}
          options={getOptionsCourses()}
          size={size}
        />
        <Sc.SubmitButton
          dataTestId={'submit-course-button'}
          disabled={!course.id}
          label={t('Siguiente')}
          loading={isLoadingAddCourse || isLoadingAddCourseStudent}
          onClick={handleOnSubmit}
          size={size}
        />
        <Sc.CancelButton
          dataTestId={'cancel-course-button'}
          label={t('Indicarlo más tarde')}
          onClick={handleCancel}
          size={size}
          variant='text'
        />
      </Sc.Form>
    </Sc.Container>
  )
}

export default CourseForm
